import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Tag,
  Button,
  DatePicker,
  Statistic,
  Table,
  Typography,
  Space,
  Spin,
} from "antd";
import {
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { selectAuthInfo } from "../../Auth/authSelectors";
import { fetchDeliveryDashboard } from "../deliveryThunks";
import { selectDeliveryDashboard, selectDeliveryLoading } from "../deliverySelectors";

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const STATUS_LABELS = {
  picking: "Picking",
  picking_verification: "Picking Verification",
  tatmeen_verification: "Tatmeen Verification",
  packing: "Packing",
  on_transit: "On Transit",
  delivered: "Delivered",
  completed: "Completed",
  cancelled: "Cancelled",
};

const STATUS_COLORS = {
  picking: "blue",
  picking_verification: "purple",
  tatmeen_verification: "cyan",
  packing: "blue",
  on_transit: "geekblue",
  delivered: "green",
  completed: "success",
  cancelled: "red",
};

const PICKUP_STATUS_LABELS = {
  picked: "Picked",
  in_transit: "In Transit",
  delivered: "Delivered",
  verify: "Verify",
  tatmeen: "Tatmeen",
  restored: "Restored",
};

const PICKUP_STATUS_COLORS = {
  picked: "processing",
  in_transit: "warning",
  delivered: "blue",
  verify: "purple",
  tatmeen: "geekblue",
  restored: "success",
};

const ALL_STATUSES = Object.keys(STATUS_LABELS);
const PICKUP_ALL_STATUSES = Object.keys(PICKUP_STATUS_LABELS);

const getVisibleStatuses = (authInfo) => {
  const roleStr = authInfo?.userInfo?.user_role_names || "";
  const roles = roleStr.split(",").map((r) => r.trim().toLowerCase());

  const isAdmin =
    roles.some((r) => r.includes("admin") || r.includes("delivery_admin"));

  if (isAdmin) return ALL_STATUSES;

  const visible = new Set();

  if (roles.some((r) => r.includes("picker"))) {
    visible.add("picking");
  }
  if (roles.some((r) => r.includes("picking_verifier"))) {
    visible.add("picking_verification");
  }
  if (roles.some((r) => r.includes("packing_verifier"))) {
    visible.add("tatmeen_verification");
  }
  if (roles.some((r) => r.includes("packer"))) {
    visible.add("packing");
  }
  if (roles.some((r) => r.includes("deliverer") || r.includes("delivery_agent"))) {
    visible.add("on_transit");
    visible.add("delivered");
  }

  return visible.size > 0 ? Array.from(visible) : ALL_STATUSES;
};

const PickupKanbanCard = ({ request }) => (
  <Card
    size="small"
    style={{ marginBottom: 8, borderRadius: 6, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}
    bodyStyle={{ padding: "10px 12px" }}
  >
    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
      {request.request_no || `#${request.id}`}
    </div>
    {(request.customer_name || request.pickup_address) && (
      <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
        {request.customer_name || request.pickup_address}
      </div>
    )}
    {request.assigned_user && (
      <div style={{ fontSize: 11, color: "#888" }}>
        <UserOutlined /> {request.assigned_user}
      </div>
    )}
  </Card>
);

const PickupKanbanColumn = ({ status, requests }) => (
  <div
    style={{
      minWidth: 200,
      maxWidth: 240,
      background: "#f5f6fa",
      borderRadius: 8,
      padding: "10px 8px",
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
      <Text strong style={{ fontSize: 13 }}>
        {PICKUP_STATUS_LABELS[status] || status}
      </Text>
      <Tag color={PICKUP_STATUS_COLORS[status] || "default"} style={{ marginLeft: 6 }}>
        {requests.length}
      </Tag>
    </div>
    <div style={{ overflowY: "auto", maxHeight: 500 }}>
      {requests.length === 0 ? (
        <div style={{ textAlign: "center", color: "#bbb", padding: "20px 0", fontSize: 12 }}>
          No requests
        </div>
      ) : (
        requests.map((req) => <PickupKanbanCard key={req.id} request={req} />)
      )}
    </div>
  </div>
);

const KanbanCard = ({ job, navigate }) => (
  <Card
    size="small"
    style={{
      marginBottom: 8,
      borderRadius: 6,
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    }}
    bodyStyle={{ padding: "10px 12px" }}
  >
    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
      {job.voucher_no || job.voucher_number || `#${job.id}`}
    </div>
    <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
      {job.customer_name || job.customer || "-"}
    </div>
    {job.delivery_type && (
      <Tag color="blue" style={{ fontSize: 11, marginBottom: 4 }}>
        {job.delivery_type}
      </Tag>
    )}
    {job.assigned_user && (
      <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>
        <UserOutlined /> {job.assigned_user}
      </div>
    )}
    {job.delivery_date && (
      <div style={{ fontSize: 11, color: "#888", marginBottom: 6 }}>
        {dayjs(job.delivery_date).format("DD MMM YYYY")}
      </div>
    )}
    <Button
      size="small"
      type="primary"
      onClick={() => navigate(`/delivery/jobs/${job.id}`)}
    >
      View
    </Button>
  </Card>
);

const KanbanColumn = ({ status, jobs, navigate }) => (
  <div
    style={{
      minWidth: 200,
      maxWidth: 260,
      flexBasis: 220,
      background: "#f5f6fa",
      borderRadius: 8,
      padding: "10px 8px",
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 10,
      }}
    >
      <Text strong style={{ fontSize: 13 }}>
        {STATUS_LABELS[status]}
      </Text>
      <Tag color={STATUS_COLORS[status]} style={{ marginLeft: 6 }}>
        {jobs.length}
      </Tag>
    </div>
    <div style={{ overflowY: "auto", maxHeight: 500 }}>
      {jobs.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            color: "#bbb",
            padding: "20px 0",
            fontSize: 12,
          }}
        >
          No jobs
        </div>
      ) : (
        jobs.map((job) => (
          <KanbanCard key={job.id} job={job} navigate={navigate} />
        ))
      )}
    </div>
  </div>
);

const DeliveryDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authInfo = useSelector(selectAuthInfo);
  const dashboard = useSelector(selectDeliveryDashboard);
  const loading = useSelector(selectDeliveryLoading);

  const [dateRange, setDateRange] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);

  const fetchDashboard = (range) => {
    const [start, end] = range || dateRange;
    dispatch(
      fetchDeliveryDashboard({
        from: start.format("YYYY-MM-DD"),
        to: end.format("YYYY-MM-DD"),
      })
    );
  };

  useEffect(() => {
    fetchDashboard(dateRange);
  }, []);

  const handleDateChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
      fetchDashboard(dates);
    }
  };

  const enabledSteps = dashboard?.enabled_steps;
  const visibleStatuses = getVisibleStatuses(authInfo).filter(
    (s) => !enabledSteps || enabledSteps.includes(s)
  );
  const kanbanData = dashboard?.kanban || {};
  const pickupKanban = dashboard?.pickup_kanban || {};
  const pickupEnabledSteps = dashboard?.pickup_enabled_steps;
  const visiblePickupStatuses = PICKUP_ALL_STATUSES.filter(
    (s) => !pickupEnabledSteps || pickupEnabledSteps.includes(s)
  );

  const topCustomersColumns = [
    {
      title: "Customer",
      dataIndex: "customer_name",
      key: "customer_name",
    },
    {
      title: "Deliveries",
      dataIndex: "count",
      key: "count",
      align: "right",
    },
  ];

  const dayWiseColumns = [
    { title: "Date", dataIndex: "date", key: "date" },
    { title: "Total", dataIndex: "total", key: "total", align: "right" },
    {
      title: "Completed",
      dataIndex: "completed",
      key: "completed",
      align: "right",
    },
  ];

  return (
    <div style={{ padding: "0 4px" }}>
      {/* Date Range Filter */}
      <Row justify="end" style={{ marginBottom: 16 }}>
        <Col xs={24} sm={"auto"}>
          <RangePicker
            value={dateRange}
            onChange={handleDateChange}
            format="DD MMM YYYY"
            style={{ width: "100%" }}
          />
        </Col>
      </Row>

      {/* Stats */}
      <Spin spinning={loading}>
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          <Col xs={12} sm={12} md={6}>
            <Card style={{ height: "100%" }}>
              <Statistic
                title="Total Deliveries"
                value={dashboard?.stats?.total_deliveries || 0}
                prefix={<ShoppingOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card style={{ height: "100%" }}>
              <Statistic
                title="Avg Delivery Time (min)"
                value={
                  dashboard?.stats?.avg_delivery_time_minutes != null
                    ? Number(dashboard.stats.avg_delivery_time_minutes).toFixed(
                        1
                      )
                    : "-"
                }
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              title={<Space><UserOutlined />Top 3 Delivery Agents</Space>}
              size="small"
              style={{ height: "100%" }}
            >
              {(dashboard?.top_agents || []).slice(0, 3).length > 0 ? (
                (dashboard.top_agents || []).slice(0, 3).map((agent, idx) => (
                  <div
                    key={agent.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "4px 0",
                      borderBottom: idx < 2 ? "1px solid #f0f0f0" : "none",
                    }}
                  >
                    <Space size={6}>
                      <span style={{ fontSize: 14 }}>
                        {idx === 0 ? "🥇" : idx === 1 ? "🥈" : "🥉"}
                      </span>
                      <Text style={{ fontSize: 13 }}>{agent.full_name}</Text>
                    </Space>
                    <Tag color="blue" style={{ fontSize: 11 }}>
                      {agent.completed_count}
                    </Tag>
                  </div>
                ))
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>No data</Text>
              )}
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card style={{ height: "100%" }}>
              <Statistic
                title="Completed"
                value={dashboard?.stats?.completed_count || 0}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Kanban Board */}

        {/* Pickup Pipeline */}
        {visiblePickupStatuses.length > 0 && (
          <Card
            title="Pickup Pipeline"
            style={{ marginBottom: 20 }}
            bodyStyle={{ padding: "12px 16px" }}
          >
            <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8 }}>
              {visiblePickupStatuses.map((status) => (
                <PickupKanbanColumn
                  key={status}
                  status={status}
                  requests={pickupKanban[status] || []}
                />
              ))}
            </div>
          </Card>
        )}
        
        <Card
          title="Delivery Pipeline"
          style={{ marginBottom: 20 }}
          bodyStyle={{ padding: "12px" }}
        >
          <div
            style={{
              display: "flex",
              gap: 12,
              overflowX: "auto",
              paddingBottom: 8,
            }}
          >
            {visibleStatuses.map((status) => {
              const jobsForStatus = kanbanData[status] || [];
              return (
                <KanbanColumn
                  key={status}
                  status={status}
                  jobs={jobsForStatus}
                  navigate={navigate}
                />
              );
            })}
          </div>
        </Card>        

        {/* Bottom section: Top Customers + Day-by-Day */}
        <Row gutter={[16, 16]}>
          <Col xs={24} md={10}>
            <Card title="Top 5 Customers">
              <Table
                size="small"
                dataSource={dashboard?.top_customers || []}
                columns={topCustomersColumns}
                scroll={{ x: "max-content" }}
                rowKey={(r) => r.customer_name || r.customer_id}
                pagination={false}
                locale={{ emptyText: "No data" }}
              />
            </Card>
          </Col>
          <Col xs={24} md={14}>
            <Card title="Deliveries by Day">
              <Table
                size="small"
                dataSource={dashboard?.by_day || []}
                columns={dayWiseColumns}
                scroll={{ x: "max-content" }}
                rowKey={(r) => r.date}
                pagination={false}
                locale={{ emptyText: "No data" }}
              />
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default DeliveryDashboard;
